import React, { useState, useRef, useEffect } from "react";
import { FaFileExcel, FaFile, FaCalendarAlt } from "react-icons/fa";
import "../../styles/common/index.css"; // Common styles (PageLayout, ActionBar, FilterForm)
import "./Export.css"; // Page-specific styles

const Export = () => {
  const [exportData, setExportData] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);
  const dropdownId = useRef("export-dropdown-" + Math.random().toString(36).substr(2, 9));

  // Listen for other dropdowns opening and close this one
  useEffect(() => {
    const handleOtherDropdownOpen = (event) => {
      if (event.detail !== dropdownId.current && isDropdownOpen) {
        setIsDropdownOpen(false);
      }
    };

    window.addEventListener("dropdown:open", handleOtherDropdownOpen);
    return () => {
      window.removeEventListener("dropdown:open", handleOtherDropdownOpen);
    };
  }, [isDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    // Use setTimeout to avoid immediate closure
    setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 0);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleOptionSelect = (option, e) => {
    e?.stopPropagation();
    if (option !== "SELECT") {
      setExportData(option);
    } else {
      setExportData("");
    }
    setIsDropdownOpen(false);
  };

  const toggleDropdown = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const willBeOpen = !isDropdownOpen;
    setIsDropdownOpen(willBeOpen);
    
    // Dispatch event to close other dropdowns
    if (willBeOpen) {
      window.dispatchEvent(new CustomEvent("dropdown:open", { detail: dropdownId.current }));
    }
  };

  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
  };

  const handleExport = () => {
    if (!exportData) {
      alert("Please select Export Data");
      return;
    }
    if (!fromDate) {
      alert("Please select From Date");
      return;
    }
    if (!toDate) {
      alert("Please select To Date");
      return;
    }
    // TODO: Implement export functionality
    console.log("Export Data:", exportData);
    console.log("From Date:", fromDate);
    console.log("To Date:", toDate);
    alert(`Exporting ${exportData} from ${fromDate} to ${toDate}`);
  };

  const exportOptions = [
    "SELECT",
    "A/C REPORTS",
    "BLACK LISTED",
    "BOB BANK CMS SHEET",
    "BOB DISBURSAL",
    "BOB RECOVERY",
    "CALL LOAN FOLLOWUP",
    "CHECK COLLECTION REPORT",
    "CIBIL REPORT",
    "COLLECTION",
    "LEAD DUPLICATE",
    "LEAD REJECTED",
    "LEAD TOTAL",
    "LEGAL NOTICE DATA",
    "LOAN CLOSE",
    "LOAN DISBURSE HOLD",
    "LOAN DISBURSE SEND BACK",
    "LOAN DISBURSED",
    "LOAN PENDING",
    "LOAN RECOVERY",
    "LOAN REPAYMENT",
    "LOAN SANCTIONED",
    "MIS REPORT",
    "PAYMENT RECEIVED",
    "RECOVERY REPORT",
    "REJECTED APPLICATIONS",
    "SANCTIONED LOANS",
  ];

  return (
    <div className="page-wrapper export-page">
      {/* Page Header */}
      <div className="page-header-section">
        <div className="page-title-section">
          <h1 className="export-page-title">Export Data</h1>
          <p className="page-subtitle">Export data reports in various formats</p>
        </div>
      </div>

      {/* Export Form Section */}
      <div className="export-form-section">
        <div className="export-form-container">
          <div className="export-form-row">
            {/* Export Data Dropdown */}
            <div className="export-form-field">
              <label>Export Data</label>
              <div className={`custom-dropdown-wrapper ${isDropdownOpen ? 'active' : ''}`} ref={dropdownRef}>
                <div
                  className="custom-dropdown-trigger"
                  onClick={toggleDropdown}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <span className={!exportData ? "placeholder" : ""}>
                    {exportData || "SELECT"}
                  </span>
                  <span className="dropdown-arrow">▼</span>
                </div>
                {isDropdownOpen && (
                  <div className="custom-dropdown-menu">
                    {exportOptions.map((option, index) => (
                      <div
                        key={index}
                        className={`custom-dropdown-option ${
                          exportData === option || (!exportData && option === "SELECT")
                            ? "selected"
                            : ""
                        }`}
                        onClick={(e) => handleOptionSelect(option, e)}
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* From Date */}
            <div className="export-form-field">
              <label>From Date</label>
              <div 
                className="date-input-wrapper" 
                onClick={() => {
                  fromDateRef.current?.focus();
                  if (fromDateRef.current && 'showPicker' in fromDateRef.current) {
                    fromDateRef.current.showPicker();
                  }
                }}
              >
                <input
                  ref={fromDateRef}
                  type="date"
                  className="export-date-input"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  placeholder="dd-mm-yyyy"
                />
                <FaCalendarAlt className="date-calendar-icon" />
              </div>
            </div>

            {/* To Date */}
            <div className="export-form-field">
              <label>To Date</label>
              <div 
                className="date-input-wrapper" 
                onClick={() => {
                  toDateRef.current?.focus();
                  if (toDateRef.current && 'showPicker' in toDateRef.current) {
                    toDateRef.current.showPicker();
                  }
                }}
              >
                <input
                  ref={toDateRef}
                  type="date"
                  className="export-date-input"
                  value={toDate}
                  onChange={handleToDateChange}
                  min={fromDate}
                  placeholder="dd-mm-yyyy"
                />
                <FaCalendarAlt className="date-calendar-icon" />
              </div>
            </div>

            {/* Export Button */}
            <div className="export-button-field">
              <button
                className="export-submit-btn"
                onClick={handleExport}
                disabled={!exportData || !fromDate || !toDate}
              >
                <FaFile className="export-icon" />
                EXPORT
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Export;

